import React, {useState} from 'react';
import {Col, Pagination, Row, Table} from 'react-bootstrap';

import {EmptyMessageDisplay} from '../EmptyMessageDisplay';
import {GenericTableCellRenderer} from "./GenericTableCellRenderer";
import {Glyphicon} from "../GlyphIcon";
import {Conditional} from "../../Conditional";
import {useTableFilterValues} from "../../../Hooks/TableHooks";


export const GenericTable = (props) => {

    const {
        Data = [],
        OnFilterUpdate,
        OnEditClick,
        ReloadData,
        FilterComponent,
        columnDefs = [],
        defaultFilterValues = {},
        stickyHeader = false,
    } = props;

    const [activePage, setActivePage] = useState(props?.activePage ?? 1);
    const [pageSize, setPageSize] = useState(props?.pageSize ?? 10);
    const [filters, setFilters] = useState([]);
    const [sortColumn, setSortColumn] = useState(props.sortColumn ?? null);
    const [sortAsc, setSortAsc] = useState(props.sortAsc ?? false);

    const [filterValues, setFilterValue, setFilterValues] = useTableFilterValues(defaultFilterValues);

    const getKeyForRow = (x, i) => {
        return props?.getKeyForRow(x) ?? i;
    }

    const filterData = (data) => {

        if(data == null) { return data; }

        // apply the active filters
        const activeFilters = filters.filter(x => x.isSet);
        data = data.filter(x => activeFilters.every(filter => filter.logic(x)));

        if(sortColumn) {
            const sortColumnDef = columnDefs?.find(z => z.key === sortColumn);
            const sortColumnLogic = sortColumnDef?.sortLogic ?? sortColumnDef?.logic;
            if (sortColumnLogic) {
                return data.toSorted((x, y) => {

                        let xValue = sortColumnLogic(x, sortAsc);
                        let yValue = sortColumnLogic(y, sortAsc);

                        if (!isNaN(xValue) && !isNaN(yValue)) {
                            xValue = Number.parseFloat(xValue);
                            yValue = Number.parseFloat(yValue);

                            return sortAsc ?
                                (xValue > yValue ? 1 : -1) :
                                (xValue < yValue ? 1 : -1);
                        } else {
                            xValue = xValue?.toString() ?? '';
                            yValue = yValue?.toString() ?? '';
                            return sortAsc ?
                                xValue.localeCompare(yValue, 'en', { numeric: true }) :
                                yValue.localeCompare(xValue, 'en', { numeric: true });
                        }
                    }
                );
            }
        }
        return data;
    }

    //internal methods
    const totalPages = Data ? Math.ceil(filterData(Data).length / pageSize) : 0;

    const getPageItem = (index) => {
        return <Pagination.Item
            key={index}
            active={index===(activePage-1)}
            onClick={(event) => { changePage(index+1) }}
        >{index+1}</Pagination.Item>;
    }

    const getPageItems = () => {
        const pageIndexes = Array.from(Array(totalPages).keys());
        const lastPageIndex = pageIndexes.length-1;

        if(lastPageIndex < 1) {
            return [];
        }

        const numberOfPagesOnEitherSide = 2;

        const first = getPageItem(0);
        const last = getPageItem(lastPageIndex);
        const middle = [
            ...pageIndexes.slice(
                Math.max((activePage-1) - numberOfPagesOnEitherSide, 1),
                Math.min((activePage-1) + (numberOfPagesOnEitherSide+1), pageIndexes.length - 1)
            )
        ]

        return <React.Fragment>
            <Pagination.First onClick={() => changePage(0)} />
            <Pagination.Prev onClick={() => changePage(activePage-1)} />
            {first}
            <Conditional Condition={middle.length}>
                {middle[0] === 1 ? null : <Pagination.Ellipsis />}
                {middle.map((pageNumber) => {
                    return getPageItem(pageNumber);
                })}
                {middle[middle.length-1] === (lastPageIndex-1) ? null : <Pagination.Ellipsis />}
            </Conditional>
            {last}
            <Pagination.Next onClick={() => changePage(activePage+1)} />
            <Pagination.Last onClick={() => changePage(lastPageIndex+1)} />
        </React.Fragment>;
    }

    const changePage = (x) => {
        setActivePage(Math.max(Math.min(x, totalPages), 1));
    }

    //wrappers around table content fetcher


    const paginateData = (data) => {
        const page = Math.min(totalPages, activePage);
        return data?.slice((page - 1) * pageSize, page * pageSize);
    }

    const clearFilters = () => {
        const newFilters = filters.map(x => {
            if (x != null) {
                x.isSet = false;
            }
            return x;
        });

        setFilters(newFilters);
        setFilterValues({});
        OnFilterUpdate?.(newFilters);
    }

    const updateFilters = (filters) => {
        setFilters(filters);
        OnFilterUpdate?.(filters);
    }

    const reloadData = () => {
        ReloadData?.();
    }

    const getNoDataMessage = () => {
        const { NoDataMessage, HasFilters, ClearFilters,
            itemNamePlural = "items",
            usesContext = true,
            emptyMessage = null,
            emptyMessageFooter = null
        } = props;

        if(NoDataMessage) {
            return NoDataMessage;
        }

        return <EmptyMessageDisplay
            ItemName={itemNamePlural}
            HasFilters={ HasFilters ?? ( filters?.find((x) => x.isSet ) !== undefined ) }
            ClearFilters={ ClearFilters ?? clearFilters }
            usesContext={usesContext}
            message={emptyMessage}
            footer={emptyMessageFooter}
        />;
    }

    const _renderFilters = () => {
        if (FilterComponent) {
            return <Row key="filters" className="filters">
                <FilterComponent
                    UpdateFilters={updateFilters}
                    OnEditClick={OnEditClick}
                    Data={Data}
                    Filters={filters}
                    filterValues={filterValues}
                    setFilterValue={setFilterValue}
                />
            </Row>;
        }

        return <Row key="filters" />;
    }

    const getTableClassName = () => {
        return (props?.className ?? "");
    }

    const getTableHeaderClassName = (columnDef) => {
        return [(columnDef?.headerClassName ?? ""), props.headerClassName ?? ""].join(' ');
    }

    const getDefaultCellRenderer = () => {
        return props.defaultCellRenderer ?? GenericTableCellRenderer;
    }

    const _renderTable = (data) => {
        
        const tableSectionLookup = {
            'header': 0, 'body': 1, 'footer': 2
        };
        
        const [headRows, bodyRows, footerRows] = data.reduce((acc, x, r) => {
                const row = <tr key={getKeyForRow(x, r)}>
                    {
                        columnDefs.map((columnDef, c) => {
                            
                            const {
                                renderer: Renderer,
                                cellRenderer,
                                key,
                                logic,
                                customData,
                                cellClassName,
                            } = columnDef;
                            
                            let CellRenderer = cellRenderer ?? getDefaultCellRenderer();
                            
                            const rendererProps = (typeof columnDef.rendererProps === 'function')
                                ? (columnDef.rendererProps(x))
                                : (columnDef.rendererProps ?? {});
                            
                            return <CellRenderer
                                key={key ?? c}
                                className={cellClassName}
                                Value={logic(x)}
                                rowData={x}
                                CustomData={customData || {}}
                            >
                                <Renderer
                                    Value={logic(x)}
                                    rowData={x}
                                    CustomData={customData || {}}
                                    {...props}
                                    {...rendererProps}
                                />
                            </CellRenderer>;
                        })
                    }
                </tr>;
                const tableSection = tableSectionLookup[x?.sbTableSection ?? 'body'];
                acc[tableSection].push(row);
                return acc;
            },
            [[],[],[]]
        );

        return (
            <Table className={getTableClassName()} key="table" striped size="sm" hover>
                <thead className={stickyHeader ? "sticky" : null}>
                <tr>
                    {
                        columnDefs.map((x, i) =>
                            <th key={x?.key ?? i}
                                className={ getTableHeaderClassName(x) + (x?.sortable ? " sortable" : "") }
                                onClick={() => {
                                    if (x?.sortable && sortColumn === x?.key) {
                                        setSortAsc(!sortAsc);
                                    } else if (x?.sortable) {
                                        setSortColumn(x?.key);
                                    }
                                }}>
                                <div className="sb-table-header-flex-div">
                                    <span className="sb-table-header-title" title={x.title}>{x.title}</span>
                                    {
                                        x.sortable ?
                                            (
                                                x.key === sortColumn ?
                                                    (
                                                        sortAsc ?
                                                            <Glyphicon glyph="sort-alpha-down" className="sb-table-header-sort-icon"/> :
                                                            <Glyphicon glyph="sort-alpha-up"  className="sb-table-header-sort-icon"/>
                                                    ) :
                                                    <Glyphicon glyph="sort" className="sb-table-header-sort-icon"/>
                                            ):
                                            null
                                    }
                                </div>
                            </th>
                        )
                    }
                </tr>
                {headRows}
                </thead>
                <tbody>
                { bodyRows}
                </tbody>
            </Table>
        );
    }

    const _renderPagination = () => {
        return <Row key="pagination">
            <Col xs={12}>
                <Pagination>
                    { getPageItems() }
                </Pagination>
            </Col>
        </Row>;
    }


    const getLoadingMessage = () => {
        return <EmptyMessageDisplay
            message="Loading..."
        />
    }

    const data = filterData(Data);
    const paginatedData = paginateData(data);
    return data?.length ?
        <div className="streambank-generic-table">
            {_renderFilters()}
            {_renderTable(paginatedData)}
            {_renderPagination()}
        </div>:
        <div className="streambank-generic-table">
            {_renderFilters()}
            <div className="sb-table-no-content-message">
                { !!data ?
                    getNoDataMessage() :
                    getLoadingMessage()
                }
            </div>
        </div>;

}
